import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  FileText,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  TriangleAlert,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Alert } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { consumer } from "@/lib/actioncable"
import {
  AnnotatedText,
  type BriefStatus,
  type Citation,
  StatusBadge,
  actionStatusTone,
  confidenceTone,
  isActiveStatus,
  priorityTone,
} from "./brief-shared"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type BriefDetail = {
  id: number
  period_label: string
  period_start: string
  period_end: string
  period_grain: string
  status: BriefStatus
  trigger: string
  model: string | null
  md_body: string | null
  input_tokens: number
  output_tokens: number
  cost: number
  validation: { passed?: boolean; regenerated?: boolean; unknown_tokens?: string[] } | null
  error_message: string | null
  generated_at: string | null
  published_at: string | null
  created_at: string
  created_by: string | null
}

type Foresight = {
  id: number
  statement: string
  confidence: string
  horizon_months: number
  reasoning: string | null
  source_signal_ids: number[]
}

type ActionItemRow = {
  id: number
  title: string
  rationale: string | null
  owner_kind: string
  owner_employee_id: number | null
  owner_employee_name: string | null
  account_id: number | null
  account_code: string | null
  account_name: string | null
  priority: string
  status: string
  due_hint: string | null
  position: number
}

type Signal = {
  id: number
  signal_type: string
  metric_kind: string
  direction: string | null
  magnitude: number | null
  current_value: number | null
  prior_value: number | null
  dimension_ref: Record<string, unknown>
  driver_label: string
  account_code: string | null
  account_name: string | null
}

type Props = {
  brief: BriefDetail
  synthesis: string
  foresights: Foresight[]
  action_items: ActionItemRow[]
  signals: Signal[]
  citation_index: Record<string, Citation[]>
  dq_flags: { metric?: string; flag?: string }[]
  account_options: { id: number; code: string; name: string }[]
  employee_options: { id: number; name: string; job_title: string | null }[]
  enums: { confidences: string[]; priorities: string[]; statuses: string[] }
  can_edit: boolean
  can_publish: boolean
  can_regenerate: boolean
  assembler_enabled: boolean
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AdminBriefsShow(props: Props) {
  const {
    brief,
    synthesis,
    foresights,
    action_items,
    signals,
    citation_index,
    dq_flags,
    account_options,
    employee_options,
    enums,
    can_edit,
    can_publish,
    can_regenerate,
    assembler_enabled,
  } = props

  const [liveStatus, setLiveStatus] = React.useState<BriefStatus>(brief.status)
  React.useEffect(() => setLiveStatus(brief.status), [brief.status])

  // Live progress: while the pipeline runs, subscribe and reload when it lands.
  const active = isActiveStatus(liveStatus, assembler_enabled)
  React.useEffect(() => {
    if (!active) return
    const sub = consumer.subscriptions.create(
      { channel: "BriefRunChannel", brief_id: brief.id },
      {
        received(data: { status: BriefStatus }) {
          setLiveStatus(data.status)
          if (!isActiveStatus(data.status, assembler_enabled)) {
            router.reload()
          }
        },
      },
    )
    return () => {
      sub.unsubscribe()
    }
  }, [active, brief.id, assembler_enabled])

  const unknownTokens = brief.validation?.unknown_tokens ?? []

  return (
    <>
      <Head title={`Brief ${brief.period_label}`}>
        <meta name="description" content={`Brief mingguan ${brief.period_label} — sintesis, foresight, dan action items dengan sitasi.`} />
        <meta property="og:title" content={`Brief ${brief.period_label}`} />
        <meta property="og:description" content={`Brief mingguan ${brief.period_label} — sintesis, foresight, dan action items dengan sitasi.`} />
      </Head>
      <AppShell width="default">
        <div className="mb-6">
          <Link href="/admin/briefs" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-accent">
            <ArrowLeft className="h-4 w-4" />
            Kembali ke daftar brief
          </Link>
        </div>

        {/* Header */}
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <FileText className="mt-1 h-6 w-6 shrink-0 text-ink-muted" />
              <div>
                <h1>Brief {brief.period_label}</h1>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <StatusBadge status={liveStatus} />
                  <Badge tone="muted">{brief.trigger === "scheduled" ? "Terjadwal" : "Manual"}</Badge>
                  {brief.created_by && <span className="text-xs text-ink-muted">oleh {brief.created_by}</span>}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {can_regenerate && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => router.post(`/admin/briefs/${brief.id}/generate`)}
                >
                  <RefreshCw className="h-4 w-4" />
                  Generate ulang
                </Button>
              )}
              {(brief.md_body || foresights.length > 0) && (
                <>
                  <a
                    href={`/admin/briefs/${brief.id}/export?format=md`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-surface px-3 py-1.5 text-sm text-ink-body hover:text-accent"
                  >
                    <Download className="h-4 w-4" />
                    .md
                  </a>
                  <a
                    href={`/admin/briefs/${brief.id}/export?format=docx`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-surface px-3 py-1.5 text-sm text-ink-body hover:text-accent"
                  >
                    <Download className="h-4 w-4" />
                    .docx
                  </a>
                </>
              )}
              {can_publish && (
                <Button
                  variant="primary"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => {
                    if (window.confirm("Publikasikan brief ini? Setelah terbit tidak bisa diedit.")) {
                      router.patch(`/admin/briefs/${brief.id}/publish`)
                    }
                  }}
                >
                  <Send className="h-4 w-4" />
                  Publikasikan
                </Button>
              )}
            </div>
          </div>

          {/* Run metadata strip */}
          {brief.generated_at && (
            <p className="mt-3 text-xs text-ink-muted">
              {brief.model && <>Model {brief.model} · </>}
              {brief.input_tokens + brief.output_tokens > 0 && (
                <>{(brief.input_tokens + brief.output_tokens).toLocaleString("id-ID")} token · ${brief.cost.toFixed(4)} · </>
              )}
              Dinarasikan {formatDate(brief.generated_at)}
              {brief.published_at && <> · Terbit {formatDate(brief.published_at)}</>}
            </p>
          )}
        </div>

        {/* Banners */}
        {brief.status === "failed" && brief.error_message && (
          <Alert tone="danger" title="Brief gagal" className="mt-6" icon={<TriangleAlert className="h-4 w-4" />}>
            {brief.error_message}
          </Alert>
        )}
        {brief.status === "needs_review" && (
          <Alert tone="warning" title="Perlu ditinjau" className="mt-6" icon={<TriangleAlert className="h-4 w-4" />}>
            Validator tidak bisa mencocokkan {unknownTokens.length} angka ke context pack
            {unknownTokens.length > 0 && <> (<span className="font-medium">{unknownTokens.join(", ")}</span>)</>}. Angka
            yang ditandai kuning di bawah perlu dikoreksi sebelum dipublikasikan.
          </Alert>
        )}
        {dq_flags.length > 0 && (
          <Alert tone="info" title="Catatan kualitas data" className="mt-6">
            <ul className="ml-4 list-disc">
              {dq_flags.map((f, i) => (
                <li key={i}>{f.metric}: {f.flag}</li>
              ))}
            </ul>
          </Alert>
        )}

        {liveStatus === "signals_ready" && !assembler_enabled && (
          <Alert tone="info" title="Narasi belum dibuat" className="mt-6">
            Brief berhenti di lapisan sinyal deterministik (narasi AI nonaktif). {signals.length} sinyal
            terdeteksi — lihat di bawah. Aktifkan kill-switch lalu “Generate ulang” untuk narasi penuh.
          </Alert>
        )}

        {/* 3-part viewer / editor */}
        {(brief.md_body || foresights.length > 0 || action_items.length > 0) ? (
          <BriefBody
            brief={brief}
            synthesis={synthesis}
            foresights={foresights}
            actionItems={action_items}
            citationIndex={citation_index}
            unknownTokens={unknownTokens}
            accountOptions={account_options}
            employeeOptions={employee_options}
            enums={enums}
            canEdit={can_edit}
          />
        ) : (
          active ? (
            <p className="mt-8 text-sm text-ink-muted">Brief sedang diproses — halaman akan diperbarui otomatis…</p>
          ) : (
            <p className="mt-8 text-sm text-ink-muted">Belum ada narasi untuk brief ini.</p>
          )
        )}

        {/* Signals (traceability) */}
        {signals.length > 0 && <SignalsSection signals={signals} />}
      </AppShell>
    </>
  )
}

// ---------------------------------------------------------------------------
// Brief body — synthesis + foresights (editable together) + action items
// ---------------------------------------------------------------------------

function BriefBody({
  brief,
  synthesis,
  foresights,
  actionItems,
  citationIndex,
  unknownTokens,
  accountOptions,
  employeeOptions,
  enums,
  canEdit,
}: {
  brief: BriefDetail
  synthesis: string
  foresights: Foresight[]
  actionItems: ActionItemRow[]
  citationIndex: Record<string, Citation[]>
  unknownTokens: string[]
  accountOptions: Props["account_options"]
  employeeOptions: Props["employee_options"]
  enums: Props["enums"]
  canEdit: boolean
}) {
  const [editing, setEditing] = React.useState(false)
  const [synthesisDraft, setSynthesisDraft] = React.useState(synthesis)
  const [foresightDrafts, setForesightDrafts] = React.useState<Foresight[]>(foresights)
  const [saving, setSaving] = React.useState(false)

  function startEdit() {
    setSynthesisDraft(synthesis)
    setForesightDrafts(foresights.map((f) => ({ ...f })))
    setEditing(true)
  }

  function save() {
    setSaving(true)
    router.patch(
      `/admin/briefs/${brief.id}`,
      {
        brief: {
          synthesis: synthesisDraft,
          foresights_attributes: foresightDrafts.map((f) => ({
            id: f.id,
            statement: f.statement,
            confidence: f.confidence,
            horizon_months: f.horizon_months,
            reasoning: f.reasoning ?? "",
          })),
        },
      },
      { onSuccess: () => setEditing(false), onFinish: () => setSaving(false) },
    )
  }

  return (
    <div className="mt-8 space-y-10">
      {/* Section: Synthesis */}
      <section>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="mb-0">Sintesis</h2>
          {canEdit && !editing && (
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={startEdit}>
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
          )}
        </div>

        {editing ? (
          <textarea
            id="synthesis-editor"
            aria-label="Sintesis"
            value={synthesisDraft}
            onChange={(e) => setSynthesisDraft(e.target.value)}
            rows={8}
            className="form-control w-full font-mono text-sm"
          />
        ) : (
          <div className="space-y-3 text-ink-body">
            {splitParagraphs(synthesis).map((para, i) => (
              <p key={i}>
                <AnnotatedText text={para} citationIndex={citationIndex} unknownTokens={unknownTokens} />
              </p>
            ))}
            {synthesis.trim() === "" && <p className="text-ink-muted">—</p>}
          </div>
        )}
      </section>

      {/* Section: Foresight */}
      <section>
        <h2>Foresight</h2>
        {editing ? (
          <div className="mt-3 space-y-4">
            {foresightDrafts.length === 0 && <p className="text-sm text-ink-muted">Tidak ada foresight.</p>}
            {foresightDrafts.map((f, idx) => (
              <div key={f.id} className="rounded-md border border-hairline bg-surface p-4">
                <textarea
                  value={f.statement}
                  onChange={(e) => updateDraft(setForesightDrafts, idx, { statement: e.target.value })}
                  rows={2}
                  className="form-control w-full text-sm"
                />
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <label className="text-xs text-ink-muted">
                    Keyakinan
                    <Select
                      value={f.confidence}
                      onChange={(e) => updateDraft(setForesightDrafts, idx, { confidence: e.target.value })}
                      className="mt-1 w-32"
                    >
                      {enums.confidences.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </Select>
                  </label>
                  <label className="text-xs text-ink-muted">
                    Horizon (bulan)
                    <Input
                      type="number"
                      min={1}
                      value={f.horizon_months}
                      onChange={(e) => updateDraft(setForesightDrafts, idx, { horizon_months: parseInt(e.target.value, 10) || 1 })}
                      className="mt-1 w-24"
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            {foresights.length === 0 && <p className="text-sm text-ink-muted">Tidak ada foresight.</p>}
            {foresights.map((f) => (
              <div key={f.id} className="rounded-md border border-hairline bg-surface p-4">
                <p className="text-ink-body">
                  <AnnotatedText text={f.statement} citationIndex={citationIndex} unknownTokens={unknownTokens} />
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge tone={confidenceTone(f.confidence)}>keyakinan: {f.confidence}</Badge>
                  <Badge tone="muted">horizon {f.horizon_months} bulan</Badge>
                </div>
                {f.reasoning && (
                  <p className="mt-2 text-sm text-ink-muted">
                    <AnnotatedText text={f.reasoning} citationIndex={citationIndex} unknownTokens={unknownTokens} />
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {editing && (
        <div className="flex items-center gap-2">
          <Button variant="primary" onClick={save} disabled={saving} className="gap-1.5">
            <CheckCircle2 className="h-4 w-4" />
            {saving ? "Menyimpan…" : "Simpan perubahan"}
          </Button>
          <Button variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
            Batal
          </Button>
        </div>
      )}

      {/* Section: Action items */}
      <ActionItemsSection
        briefId={brief.id}
        items={actionItems}
        citationIndex={citationIndex}
        unknownTokens={unknownTokens}
        accountOptions={accountOptions}
        employeeOptions={employeeOptions}
        enums={enums}
        canEdit={canEdit}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Action items
// ---------------------------------------------------------------------------

type ActionItemForm = {
  title: string
  rationale: string
  priority: string
  status: string
  due_hint: string
  account_id: string
  owner_employee_id: string
}

function blankForm(enums: Props["enums"]): ActionItemForm {
  return { title: "", rationale: "", priority: "medium", status: enums.statuses[0] ?? "open", due_hint: "", account_id: "", owner_employee_id: "" }
}

function ActionItemsSection({
  briefId,
  items,
  citationIndex,
  unknownTokens,
  accountOptions,
  employeeOptions,
  enums,
  canEdit,
}: {
  briefId: number
  items: ActionItemRow[]
  citationIndex: Record<string, Citation[]>
  unknownTokens: string[]
  accountOptions: Props["account_options"]
  employeeOptions: Props["employee_options"]
  enums: Props["enums"]
  canEdit: boolean
}) {
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [editingId, setEditingId] = React.useState<number | null>(null)
  const [form, setForm] = React.useState<ActionItemForm>(blankForm(enums))
  const [saving, setSaving] = React.useState(false)

  function openCreate() {
    setEditingId(null)
    setForm(blankForm(enums))
    setDialogOpen(true)
  }

  function openEdit(item: ActionItemRow) {
    setEditingId(item.id)
    setForm({
      title: item.title,
      rationale: item.rationale ?? "",
      priority: item.priority,
      status: item.status,
      due_hint: item.due_hint ?? "",
      account_id: item.account_id ? String(item.account_id) : "",
      owner_employee_id: item.owner_employee_id ? String(item.owner_employee_id) : "",
    })
    setDialogOpen(true)
  }

  function submit() {
    if (!form.title.trim()) return
    setSaving(true)
    const payload = { action_item: form }
    const onFinish = () => setSaving(false)
    const onSuccess = () => setDialogOpen(false)
    if (editingId) {
      router.patch(`/admin/briefs/${briefId}/action_items/${editingId}`, payload, { onSuccess, onFinish })
    } else {
      router.post(`/admin/briefs/${briefId}/action_items`, payload, { onSuccess, onFinish })
    }
  }

  function changeStatus(item: ActionItemRow, status: string) {
    router.patch(`/admin/briefs/${briefId}/action_items/${item.id}`, {
      action_item: {
        title: item.title,
        rationale: item.rationale ?? "",
        priority: item.priority,
        status,
        due_hint: item.due_hint ?? "",
        account_id: item.account_id ? String(item.account_id) : "",
        owner_employee_id: item.owner_employee_id ? String(item.owner_employee_id) : "",
      },
    })
  }

  function remove(item: ActionItemRow) {
    if (!window.confirm(`Hapus action item "${item.title}"?`)) return
    router.delete(`/admin/briefs/${briefId}/action_items/${item.id}`)
  }

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="mb-0">Action Items</h2>
        {canEdit && (
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            Tambah
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-ink-muted">Tidak ada action item.</p>
      ) : (
        <ol className="space-y-3">
          {items.map((item, idx) => (
            <li key={item.id} className="rounded-md border border-hairline bg-surface p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-ink-display">
                    {idx + 1}. <AnnotatedText text={item.title} citationIndex={citationIndex} unknownTokens={unknownTokens} />
                  </p>
                  {item.rationale && (
                    <p className="mt-1 text-sm text-ink-muted">
                      <AnnotatedText text={item.rationale} citationIndex={citationIndex} unknownTokens={unknownTokens} />
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge tone={priorityTone(item.priority)}>{item.priority}</Badge>
                    {item.owner_kind === "delegate" && item.owner_employee_name ? (
                      <Badge tone="neutral">PIC: {item.owner_employee_name}</Badge>
                    ) : (
                      <Badge tone="muted">Saya</Badge>
                    )}
                    {item.account_code && <Badge tone="neutral">{item.account_code}</Badge>}
                    {item.due_hint && <span className="text-xs text-ink-muted">{item.due_hint}</span>}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex shrink-0 items-center gap-1">
                    <button onClick={() => openEdit(item)} title="Edit" className="rounded p-1 text-ink-muted hover:text-accent">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => remove(item)} title="Hapus" className="rounded p-1 text-ink-muted hover:text-red-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                {canEdit ? (
                  <Select
                    value={item.status}
                    onChange={(e) => changeStatus(item, e.target.value)}
                    className="w-40"
                  >
                    {enums.statuses.map((s) => (
                      <option key={s} value={s}>{statusLabel(s)}</option>
                    ))}
                  </Select>
                ) : (
                  <Badge tone={actionStatusTone(item.status)}>{statusLabel(item.status)}</Badge>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Action Item" : "Tambah Action Item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label htmlFor="ai-title">Judul</label>
              <Input id="ai-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label htmlFor="ai-rationale">Alasan</label>
              <textarea
                id="ai-rationale"
                value={form.rationale}
                onChange={(e) => setForm({ ...form, rationale: e.target.value })}
                rows={2}
                className="form-control mt-1 w-full text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="ai-priority">Prioritas</label>
                <Select id="ai-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="mt-1">
                  {enums.priorities.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label htmlFor="ai-status">Status</label>
                <Select id="ai-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1">
                  {enums.statuses.map((s) => (
                    <option key={s} value={s}>{statusLabel(s)}</option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="ai-account">Akun</label>
                <Select id="ai-account" value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} className="mt-1">
                  <option value="">Lintas-portofolio</option>
                  {accountOptions.map((a) => (
                    <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label htmlFor="ai-owner">PIC (delegasi)</label>
                <Select id="ai-owner" value={form.owner_employee_id} onChange={(e) => setForm({ ...form, owner_employee_id: e.target.value })} className="mt-1">
                  <option value="">Saya / otomatis dari akun</option>
                  {employeeOptions.map((e) => (
                    <option key={e.id} value={e.id}>{e.name}{e.job_title ? ` (${e.job_title})` : ""}</option>
                  ))}
                </Select>
              </div>
            </div>
            <div>
              <label htmlFor="ai-due">Tenggat (opsional)</label>
              <Input id="ai-due" value={form.due_hint} onChange={(e) => setForm({ ...form, due_hint: e.target.value })} className="mt-1" placeholder="mis. minggu ini" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Batal</Button>
            <Button variant="primary" onClick={submit} disabled={saving || !form.title.trim()}>
              {saving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Signals (traceability)
// ---------------------------------------------------------------------------

function SignalsSection({ signals }: { signals: Signal[] }) {
  return (
    <section className="mt-10">
      <h2 className="mb-4">Sinyal Terdeteksi</h2>
      <div className="overflow-hidden rounded-md border border-hairline">
        <table className="w-full text-sm">
          <thead className="bg-surface">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Tipe</th>
              <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Metrik</th>
              <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Akun</th>
              <th className="px-4 py-2.5 text-right font-medium text-ink-muted">Kini</th>
              <th className="px-4 py-2.5 text-right font-medium text-ink-muted">Sebelumnya</th>
              <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Driver</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {signals.map((s) => (
              <tr key={s.id}>
                <td className="px-4 py-2.5 text-ink-body">{s.signal_type}</td>
                <td className="px-4 py-2.5 text-ink-muted">{s.metric_kind}</td>
                <td className="px-4 py-2.5 text-ink-body">{s.account_code ?? "—"}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-ink-body">{fmtNum(s.current_value)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-ink-muted">{fmtNum(s.prior_value)}</td>
                <td className="px-4 py-2.5">
                  <Badge tone={s.driver_label === "identified" ? "success" : "muted"}>
                    {s.driver_label === "identified" ? "teridentifikasi" : "belum terjelaskan"}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function updateDraft(
  setter: React.Dispatch<React.SetStateAction<Foresight[]>>,
  index: number,
  patch: Partial<Foresight>,
) {
  setter((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)))
}

function splitParagraphs(text: string): string[] {
  const trimmed = (text ?? "").trim()
  if (!trimmed) return []
  return trimmed.split(/\n{2,}/)
}

function statusLabel(status: string): string {
  if (status === "in_progress") return "Dikerjakan"
  if (status === "done") return "Selesai"
  return "Terbuka"
}

function fmtNum(n: number | null): string {
  if (n === null || n === undefined) return "—"
  return n.toLocaleString("id-ID", { maximumFractionDigits: 2 })
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}
